import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  LayoutDashboard, Car, FileText, Settings as SettingsIcon, ShieldCheck, Plus, Pencil, Trash2,
  Upload, Loader2, CheckCircle2, XCircle, AlertTriangle, ScrollText, Download,
} from "lucide-react";

const API_STATUSES = ["Submitted", "Under Review", "Additional Information Required", "Approved", "Conditionally Approved", "Declined"];

// ---------- Stats ----------
const StatCard = ({ label, value, tone }) => (
  <div className="card-surface p-5">
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`mt-2 font-display text-3xl font-extrabold ${tone || "text-slate-900"}`}>{value ?? 0}</div>
  </div>
);

const emptyVehicle = {
  make: "", model: "", trim: "", year: 2023, mileage: 0, price: 0, engine: "", displacement: "",
  horsepower: 0, torque: "", transmission: "", drivetrain: "", fuel_type: "", mpg: "", exterior_color: "",
  interior_color: "", body_type: "", seats: 5, doors: 4, vin: "", stock_number: "", condition: "Used",
  location: "", features: [], description: "", status: "available", published: false, images: [],
};

export default function Admin() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "dashboard";
  const perms = user?.permissions || [];

  return (
    <Layout>
      <div className="container-x py-10">
        <div className="mb-8 flex items-center gap-3">
          <ShieldCheck className="h-7 w-7 text-gold" />
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Admin Portal</h1>
            <p className="text-sm text-slate-600">{user.email} · <span className="capitalize text-gold">{user.role?.replace("_", " ")}</span></p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={(t) => setParams(t === "dashboard" ? {} : { tab: t })}>
          <TabsList className="flex-wrap bg-[#FFFFFF]">
            {perms.includes("stats") && <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard" className="gap-1.5 data-[state=active]:bg-gold data-[state=active]:text-[#EEF3FC]"><LayoutDashboard className="h-4 w-4" /> Dashboard</TabsTrigger>}
            {perms.includes("vehicles") && <TabsTrigger value="vehicles" data-testid="admin-tab-vehicles" className="gap-1.5 data-[state=active]:bg-gold data-[state=active]:text-[#EEF3FC]"><Car className="h-4 w-4" /> Vehicles</TabsTrigger>}
            {perms.includes("applications") && <TabsTrigger value="applications" data-testid="admin-tab-applications" className="gap-1.5 data-[state=active]:bg-gold data-[state=active]:text-[#EEF3FC]"><FileText className="h-4 w-4" /> Applications</TabsTrigger>}
            {perms.includes("settings") && <TabsTrigger value="settings" data-testid="admin-tab-settings" className="gap-1.5 data-[state=active]:bg-gold data-[state=active]:text-[#EEF3FC]"><SettingsIcon className="h-4 w-4" /> Settings</TabsTrigger>}
            {perms.includes("audit") && <TabsTrigger value="audit" data-testid="admin-tab-audit" className="gap-1.5 data-[state=active]:bg-gold data-[state=active]:text-[#EEF3FC]"><ScrollText className="h-4 w-4" /> Audit Log</TabsTrigger>}
          </TabsList>

          {perms.includes("stats") && <TabsContent value="dashboard" className="mt-6"><StatsPanel /></TabsContent>}
          {perms.includes("vehicles") && <TabsContent value="vehicles" className="mt-6"><VehiclesPanel /></TabsContent>}
          {perms.includes("applications") && <TabsContent value="applications" className="mt-6"><ApplicationsPanel canApprove={perms.includes("approvals")} canDocs={perms.includes("documents")} /></TabsContent>}
          {perms.includes("settings") && <TabsContent value="settings" className="mt-6"><SettingsPanel /></TabsContent>}
          {perms.includes("audit") && <TabsContent value="audit" className="mt-6"><AuditPanel /></TabsContent>}
        </Tabs>
      </div>
    </Layout>
  );
}

function StatsPanel() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-8">
      <div>
        <h3 className="mb-3 font-display text-lg font-bold text-slate-900">Inventory</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Total Vehicles" value={s.total_vehicles} />
          <StatCard label="Available" value={s.available_vehicles} tone="text-emerald-400" />
          <StatCard label="Reserved" value={s.reserved_vehicles} tone="text-amber-400" />
          <StatCard label="Sold" value={s.sold_vehicles} tone="text-red-400" />
        </div>
      </div>
      <div>
        <h3 className="mb-3 font-display text-lg font-bold text-slate-900">Applications</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Total" value={s.total_applications} />
          <StatCard label="Pending" value={s.pending_applications} tone="text-amber-400" />
          <StatCard label="Approved" value={s.approved_applications} tone="text-emerald-400" />
          <StatCard label="Declined" value={s.declined_applications} tone="text-red-400" />
        </div>
      </div>
      <div>
        <h3 className="mb-3 font-display text-lg font-bold text-slate-900">Financing & Payments</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Active Financing" value={s.active_financing} tone="text-gold" />
          <StatCard label="Payments Received" value={s.payments_received} tone="text-emerald-400" />
          <StatCard label="Upcoming Payments" value={s.upcoming_payments} />
          <StatCard label="Late Payments" value={s.late_payments} tone="text-red-400" />
        </div>
      </div>
    </div>
  );
}

function VehiclesPanel() {
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => { await api.post(`/admin/vehicles/${id}/status?status=${status}`); load(); };
  const togglePublish = async (v) => {
    try { await api.post(`/admin/vehicles/${v.id}/publish?publish=${!v.published}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this vehicle?")) return; await api.delete(`/admin/vehicles/${id}`); load(); toast.success("Deleted"); };

  return (
    <div>
      <div className="mb-4 flex justify-between">
        <h3 className="font-display text-lg font-bold text-slate-900">Vehicle Management ({vehicles.length})</h3>
        <Button onClick={() => setEditing({ ...emptyVehicle })} data-testid="admin-add-vehicle-btn" className="btn-gold rounded-full"><Plus className="mr-1 h-4 w-4" /> Add Vehicle</Button>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="p-4">Vehicle</th><th className="p-4">Price</th><th className="p-4">Images</th><th className="p-4">Status</th><th className="p-4">Published</th><th className="p-4">Actions</th>
          </tr></thead>
          <tbody>
            {vehicles.map((v) => (
              <tr key={v.id} className="border-b border-slate-200/60 text-sm" data-testid={`admin-vehicle-row-${v.id}`}>
                <td className="p-4"><div className="flex items-center gap-3"><img src={imgUrl(v.images?.[0]?.url)} alt="" className="h-10 w-16 rounded object-cover" /><div><div className="font-medium text-slate-900">{v.make} {v.model}</div><div className="text-xs text-slate-500">{v.year} {v.trim}</div></div></div></td>
                <td className="p-4 font-mono text-slate-900">{fmtMoney(v.price)}</td>
                <td className="p-4"><span className={v.images?.length >= 5 ? "text-emerald-400" : "text-amber-400"}>{v.images?.length || 0}</span></td>
                <td className="p-4">
                  <Select value={v.status} onValueChange={(s) => setStatus(v.id, s)}>
                    <SelectTrigger className="h-8 w-28 border-slate-200 bg-[#EEF3FC] text-xs text-slate-900"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-slate-200 bg-[#FFFFFF] text-slate-900">{["available", "reserved", "sold"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-4"><Switch checked={v.published} onCheckedChange={() => togglePublish(v)} data-testid={`admin-publish-${v.id}`} /></td>
                <td className="p-4"><div className="flex gap-2">
                  <button onClick={() => setEditing(v)} data-testid={`admin-edit-${v.id}`} className="rounded-lg border border-slate-200 p-2 text-slate-700 hover:text-gold"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(v.id)} className="rounded-lg border border-slate-200 p-2 text-slate-700 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <VehicleModal vehicle={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function VehicleModal({ vehicle, onClose, onSaved }) {
  const [v, setV] = useState({ ...vehicle, features: vehicle.features || [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isNew = !vehicle.id;
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...v, features: typeof v.features === "string" ? v.features.split(",").map((f) => f.trim()).filter(Boolean) : v.features };
      if (isNew) await api.post("/admin/vehicles", payload);
      else await api.put(`/admin/vehicles/${v.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const uploadImg = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (isNew) { toast.error("Save the vehicle first, then upload images"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("image_type", "additional"); fd.append("file", file);
    try {
      const r = await api.post(`/admin/vehicles/${v.id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("images", [...v.images, r.data]);
      toast.success("Image uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const addUrlImage = () => {
    const url = window.prompt("Image URL");
    if (url) set("images", [...v.images, { url, type: "additional" }]);
  };

  const F = (label, key, props = {}) => (
    <div><label className="mb-1 block text-xs text-slate-600">{label}</label><Input value={v[key] ?? ""} onChange={(e) => set(key, props.type === "number" ? Number(e.target.value) : e.target.value)} className="border-slate-200 bg-[#EEF3FC] text-slate-900" {...props} /></div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-slate-200 bg-[#FFFFFF] text-slate-900">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "Add Vehicle" : "Edit Vehicle"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {F("Make", "make")}{F("Model", "model")}{F("Trim", "trim")}
          {F("Year", "year", { type: "number" })}{F("Mileage", "mileage", { type: "number" })}{F("Price", "price", { type: "number" })}
          {F("VIN", "vin")}{F("Stock #", "stock_number")}{F("Body Type", "body_type")}
          {F("Engine", "engine")}{F("Displacement", "displacement")}{F("Horsepower", "horsepower", { type: "number" })}
          {F("Torque", "torque")}{F("Transmission", "transmission")}{F("Drivetrain", "drivetrain")}
          {F("Fuel Type", "fuel_type")}{F("MPG", "mpg")}{F("Condition", "condition")}
          {F("Exterior Color", "exterior_color")}{F("Interior Color", "interior_color")}{F("Location", "location")}
          {F("Seats", "seats", { type: "number" })}{F("Doors", "doors", { type: "number" })}
        </div>
        <div><label className="mb-1 block text-xs text-slate-600">Features (comma separated)</label><Input value={Array.isArray(v.features) ? v.features.join(", ") : v.features} onChange={(e) => set("features", e.target.value)} className="border-slate-200 bg-[#EEF3FC] text-slate-900" /></div>
        <div><label className="mb-1 block text-xs text-slate-600">Description</label><Textarea rows={3} value={v.description} onChange={(e) => set("description", e.target.value)} className="border-slate-200 bg-[#EEF3FC] text-slate-900" /></div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-xs text-slate-600">Images ({v.images.length}) — min 5 to publish</label>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={addUrlImage} className="h-8 border-slate-200 bg-transparent text-xs text-slate-700 hover:text-slate-900">Add URL</Button>
              <label className="flex h-8 cursor-pointer items-center gap-1 rounded-md border border-gold/40 bg-gold/5 px-3 text-xs text-gold">
                {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload
                <input type="file" accept="image/*" onChange={uploadImg} className="hidden" />
              </label>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {v.images.map((img, i) => (
              <div key={i} className="group relative aspect-video overflow-hidden rounded border border-slate-200">
                <img src={imgUrl(img.url)} alt="" className="h-full w-full object-cover" />
                <button onClick={() => set("images", v.images.filter((_, j) => j !== i))} className="absolute right-0.5 top-0.5 rounded bg-black/70 p-0.5 text-slate-900 opacity-0 group-hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-[#EEF3FC] p-3">
          <Switch checked={v.published} onCheckedChange={(c) => set("published", c)} id="pub" />
          <Label htmlFor="pub" className="text-sm text-slate-700">Published (visible to customers)</Label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-200 bg-transparent text-slate-700 hover:text-slate-900">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="admin-save-vehicle-btn" className="btn-gold rounded-full px-6">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Vehicle"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplicationsPanel({ canApprove, canDocs }) {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("__all__");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (filter !== "__all__") q.set("status", filter);
    if (search) q.set("search", search);
    api.get(`/admin/applications?${q}`).then((r) => setApps(r.data)).catch(() => {});
  }, [filter, search]);
  useEffect(() => { load(); }, [load]);

  const STATUS_COLOR = {
    Submitted: "text-blue-400", "Under Review": "text-indigo-400", "Additional Information Required": "text-amber-400",
    Approved: "text-emerald-400", "Conditionally Approved": "text-teal-400", Declined: "text-red-400",
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-3">
        <Input placeholder="Search customer name or email…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-app-search" className="max-w-xs border-slate-200 bg-[#FFFFFF] text-slate-900" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56 border-slate-200 bg-[#FFFFFF] text-slate-900" data-testid="admin-app-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-slate-200 bg-[#FFFFFF] text-slate-900">
            <SelectItem value="__all__">All Statuses</SelectItem>
            {API_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="p-4">Applicant</th><th className="p-4">Vehicle</th><th className="p-4">Est. Monthly</th><th className="p-4">Status</th><th className="p-4">Date</th><th className="p-4"></th>
          </tr></thead>
          <tbody>
            {apps.map((a) => (
              <tr key={a.id} className="border-b border-slate-200/60 text-sm" data-testid={`admin-app-row-${a.id}`}>
                <td className="p-4"><div className="font-medium text-slate-900">{a.personal?.first_name} {a.personal?.last_name}</div><div className="text-xs text-slate-500">{a.personal?.email}</div></td>
                <td className="p-4 text-slate-700">{a.vehicle_summary.year} {a.vehicle_summary.make} {a.vehicle_summary.model}</td>
                <td className="p-4 font-mono text-slate-900">{fmtMoney(a.financing.monthly_payment)}</td>
                <td className={`p-4 font-medium ${STATUS_COLOR[a.status] || "text-slate-700"}`}>{a.status}</td>
                <td className="p-4 text-slate-600">{fmtDate(a.created_at)}</td>
                <td className="p-4"><Button size="sm" onClick={() => setSelected(a.id)} data-testid={`admin-app-view-${a.id}`} className="btn-gold rounded-full">Review</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {apps.length === 0 && <div className="p-12 text-center text-slate-600">No applications found.</div>}
      </div>
      {selected && <ApplicationModal id={selected} canApprove={canApprove} canDocs={canDocs} onClose={() => setSelected(null)} onChanged={() => { load(); }} />}
    </div>
  );
}

function ApplicationModal({ id, canApprove, canDocs, onClose, onChanged }) {
  const [a, setA] = useState(null);
  const [note, setNote] = useState("");
  const [decisionNote, setDecisionNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/applications/${id}`).then((r) => setA(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/admin/applications/${id}/decision`, { decision, notes: decisionNote });
      toast.success(`Application ${decision}`);
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const addNote = async () => { if (!note) return; await api.post(`/admin/applications/${id}/notes`, { note }); setNote(""); load(); toast.success("Note added"); };
  const requestDocs = async () => {
    const msg = window.prompt("Message to customer (what documents are needed?)");
    if (!msg) return;
    await api.post(`/admin/applications/${id}/request-documents`, { message: msg, documents: [] });
    load(); onChanged(); toast.success("Requested additional documents");
  };
  const viewDoc = (path) => {
    window.open(`${api.defaults.baseURL}/documents/download?path=${encodeURIComponent(path)}`, "_blank");
  };

  if (!a) return null;
  const p = a.personal || {};
  const e = a.employment || {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-slate-200 bg-[#FFFFFF] text-slate-900">
        <DialogHeader><DialogTitle className="font-display">Application Review</DialogTitle></DialogHeader>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-[#EEF3FC] p-4">
          <div>
            <div className="font-display text-lg font-bold text-slate-900">{p.first_name} {p.last_name}</div>
            <div className="text-sm text-slate-600">{p.email} · {p.phone}</div>
          </div>
          <Badge className="bg-gold/15 text-gold">{a.status}</Badge>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-[#EEF3FC] p-4 text-sm">
            <div className="mb-2 font-semibold text-gold">Customer</div>
            <p className="text-slate-700">{p.address}, {p.city}, {p.state} {p.zip_code}</p>
            <p className="text-slate-600">DOB: {p.date_of_birth || "—"}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-[#EEF3FC] p-4 text-sm">
            <div className="mb-2 font-semibold text-gold">Employment & Income</div>
            <p className="text-slate-700">{e.status || "—"}{e.employer ? ` · ${e.employer}` : ""}</p>
            <p className="text-slate-600">Income: {e.annual_income ? fmtMoney(e.annual_income) : "—"}{e.additional_income ? ` (+${fmtMoney(e.additional_income)})` : ""}</p>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-[#EEF3FC] p-4 text-sm">
          <div className="mb-2 font-semibold text-gold">Financing Request</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-slate-700 sm:grid-cols-3">
            <span>Vehicle: {a.vehicle_summary.year} {a.vehicle_summary.make} {a.vehicle_summary.model}</span>
            <span>Price: {fmtMoney(a.financing.vehicle_price)}</span>
            <span>Down: {fmtMoney(a.financing.down_payment)} ({a.financing.down_payment_pct}%)</span>
            <span>Financed: {fmtMoney(a.financing.amount_financed)}</span>
            <span>Term: {a.financing.term} mo</span>
            <span>APR: {a.financing.apr}% · {fmtMoney(a.financing.monthly_payment)}/mo</span>
          </div>
        </div>

        {/* Documents */}
        <div className="rounded-lg border border-slate-200 bg-[#EEF3FC] p-4 text-sm">
          <div className="mb-2 font-semibold text-gold">Documents {!canDocs && <span className="text-xs font-normal text-slate-500">(restricted to authorized roles)</span>}</div>
          {canDocs ? (
            a.documents?.length ? a.documents.map((d) => (
              <button key={d.id} onClick={() => viewDoc(d.storage_path)} data-testid={`admin-doc-${d.id}`} className="mr-2 mb-2 inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:border-gold/40 hover:text-gold">
                <Download className="h-3 w-3" /> {d.type} — {d.original_filename}
              </button>
            )) : <p className="text-slate-500">No documents uploaded.</p>
          ) : <p className="text-slate-500">You do not have permission to view customer documents.</p>}
        </div>

        {/* Internal notes */}
        <div className="rounded-lg border border-slate-200 bg-[#EEF3FC] p-4">
          <div className="mb-2 text-sm font-semibold text-gold">Internal Notes</div>
          <div className="mb-3 space-y-2">
            {a.internal_notes?.map((n) => <div key={n.id} className="text-xs text-slate-600"><span className="text-slate-500">{n.admin_email} · {fmtDate(n.at)}:</span> {n.note}</div>)}
          </div>
          <div className="flex gap-2">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add internal note…" className="border-slate-200 bg-[#FFFFFF] text-slate-900" data-testid="admin-note-input" />
            <Button onClick={addNote} variant="outline" className="border-slate-200 bg-transparent text-slate-700 hover:text-slate-900">Add</Button>
          </div>
        </div>

        {canApprove && (
          <>
            <Textarea value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} placeholder="Decision notes (recorded in audit log)…" rows={2} className="border-slate-200 bg-[#EEF3FC] text-slate-900" data-testid="admin-decision-notes" />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Button onClick={() => decide("Approved")} disabled={busy} data-testid="admin-approve-application-btn" className="rounded-full bg-emerald-600 py-6 font-bold text-white hover:bg-emerald-500"><CheckCircle2 className="mr-2 h-5 w-5" /> APPROVE APPLICATION</Button>
              <Button onClick={() => decide("Conditionally Approved")} disabled={busy} data-testid="admin-conditional-btn" className="rounded-full bg-teal-600 py-6 font-bold text-white hover:bg-teal-500"><AlertTriangle className="mr-2 h-5 w-5" /> CONDITIONALLY APPROVE</Button>
              <Button onClick={() => decide("Declined")} disabled={busy} data-testid="admin-decline-btn" className="rounded-full bg-red-600 py-6 font-bold text-white hover:bg-red-500"><XCircle className="mr-2 h-5 w-5" /> DECLINE APPLICATION</Button>
              <Button onClick={requestDocs} disabled={busy} variant="outline" data-testid="admin-request-docs-btn" className="rounded-full border-amber-500/40 bg-transparent py-6 font-bold text-amber-400 hover:bg-amber-500/10">REQUEST DOCUMENTS</Button>
            </div>
            <Button onClick={() => decide("Under Review")} disabled={busy} variant="ghost" className="text-slate-600 hover:text-slate-900">Mark as Under Review</Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-gold" />;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        whatsapp_number: s.whatsapp_number, whatsapp_message: s.whatsapp_message,
        company_phone: s.company_phone, company_email: s.company_email, company_address: s.company_address,
        min_down_payment_pct: Number(s.min_down_payment_pct), apr: Number(s.apr), max_term: Number(s.max_term),
        available_terms: s.available_terms, down_payment_options: s.down_payment_options,
        notifications_enabled: s.notifications_enabled,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const Fld = (label, key, props = {}) => (
    <div><label className="mb-1.5 block text-sm text-slate-700">{label}</label><Input value={s[key] ?? ""} onChange={(e) => set(key, e.target.value)} data-testid={`setting-${key}`} className="border-slate-200 bg-[#EEF3FC] text-slate-900" {...props} /></div>
  );

  return (
    <div className="max-w-2xl space-y-6">
      <div className="card-surface p-6">
        <h3 className="mb-4 font-display text-lg font-bold text-slate-900">WhatsApp</h3>
        <div className="space-y-4">
          {Fld("WhatsApp Business Number (digits only)", "whatsapp_number")}
          <div><label className="mb-1.5 block text-sm text-slate-700">Default WhatsApp Message</label><Textarea rows={2} value={s.whatsapp_message} onChange={(e) => set("whatsapp_message", e.target.value)} data-testid="setting-whatsapp_message" className="border-slate-200 bg-[#EEF3FC] text-slate-900" /><p className="mt-1 text-xs text-slate-500">Use [VEHICLE NAME] as a placeholder.</p></div>
        </div>
      </div>
      <div className="card-surface p-6">
        <h3 className="mb-4 font-display text-lg font-bold text-slate-900">Company</h3>
        <div className="space-y-4">{Fld("Phone", "company_phone")}{Fld("Email", "company_email")}{Fld("Address", "company_address")}</div>
      </div>
      <div className="card-surface p-6">
        <h3 className="mb-4 font-display text-lg font-bold text-slate-900">Financing Parameters</h3>
        <div className="grid grid-cols-3 gap-4">
          {Fld("Min Down Payment (%)", "min_down_payment_pct", { type: "number" })}
          {Fld("Default APR (%)", "apr", { type: "number", step: "0.1" })}
          {Fld("Max Term (months, ≤48)", "max_term", { type: "number" })}
        </div>
        <p className="mt-3 text-xs text-slate-500">Business rules are enforced server-side: minimum down payment and 48-month maximum term cannot be bypassed.</p>
      </div>
      <Button onClick={save} disabled={saving} data-testid="admin-save-settings-btn" className="btn-gold rounded-full px-10 py-6">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}</Button>
    </div>
  );
}

function AuditPanel() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  return (
    <div className="card-surface overflow-x-auto">
      <table className="w-full min-w-[640px]">
        <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
          <th className="p-4">Admin</th><th className="p-4">Action</th><th className="p-4">Application</th><th className="p-4">Notes</th><th className="p-4">Date/Time</th>
        </tr></thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-b border-slate-200/60 text-sm">
              <td className="p-4 text-slate-700">{l.admin_email}</td>
              <td className="p-4 font-medium text-gold">{l.action}</td>
              <td className="p-4 font-mono text-xs text-slate-500">{l.application_id?.slice(0, 12)}</td>
              <td className="p-4 text-slate-600">{l.notes || "—"}</td>
              <td className="p-4 text-slate-600">{new Date(l.timestamp).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {logs.length === 0 && <div className="p-12 text-center text-slate-600">No audit records yet.</div>}
    </div>
  );
}
